import { useEffect, useMemo, useRef, useState } from "react";
import {
  Archive,
  AlertTriangle,
  Check,
  Eye,
  FileText,
  FolderOpen,
  Pencil,
  Save,
  Search,
  ShieldCheck,
  Upload,
  X,
} from "lucide-react";

import { Button, Drawer } from "@/design-system/primitives";
import {
  useClinicianGuides,
  useClinicianDirectionScopes,
  useClinicianProducts,
  useUpdateClinicianGuide,
  useUploadClinicianGuide,
} from "@/lib/api/clinicianHooks";
import { apiBlob } from "@/lib/api/client";
import type { ClinicianGuide, ClinicianProduct } from "@/types/clinician";

function formatPartner(partnerKey: string) {
  return (
    {
      precisionmeds: "Precision Medicine",
      the_pharmacy_hub: "The Pharmacy Hub / Olympia",
      optiorx: "OptioRx",
    }[partnerKey] ?? partnerKey.replaceAll("_", " ")
  );
}

export function GuidesScreen() {
  const guides = useClinicianGuides();
  const products = useClinicianProducts();
  const directionScopes = useClinicianDirectionScopes();
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState("");
  const [folder, setFolder] = useState<"all" | "guide" | "coa">("all");
  const partnerKeys = useMemo(
    () =>
      Array.from(
        new Set(
          (directionScopes.data ?? [])
            .map((scope) => scope.partner_key)
            .filter(Boolean),
        ),
      ).sort(),
    [directionScopes.data],
  );
  const productById = useMemo(
    () =>
      new Map(
        (products.data ?? []).map((product) => [product.product_id, product]),
      ),
    [products.data],
  );
  const filteredGuides = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return (guides.data ?? []).filter((guide) => {
      if (folder !== "all" && guide.document_kind !== folder) return false;
      if (!query) return true;
      return [
        guide.title,
        guide.description,
        guide.filename,
        guide.document_kind,
        guide.coa_scope,
        guide.lot_number,
        ...guide.partner_keys,
        ...guide.product_ids.flatMap((productId) => [
          productId,
          productById.get(productId)?.label,
        ]),
      ].some((value) => value?.toLocaleLowerCase().includes(query));
    });
  }, [folder, guides.data, productById, search]);

  return (
    <div className="max-w-[100rem] mx-auto px-14 md:px-30 xl:px-40 py-24 md:py-40 flex flex-col gap-20">
      <header className="flex flex-wrap items-end justify-between gap-12">
        <div className="flex flex-col gap-5">
          <span className="type-label-sm text-ink-muted">
            TEMPLATES · GUIDES
          </span>
          <h1 className="type-heading-xl">PDF library</h1>
          <p className="type-body-sm text-ink-muted max-w-[68rem]">
            Patient guides and pharmacy-specific Certificates of Analysis that
            clinicians can securely attach in chat. Each send creates a
            patient-scoped portal link; the source PDF stays private.
          </p>
        </div>
        <Button type="button" size="sm" onClick={() => setAdding(true)}>
          <Upload size={13} /> Upload PDF
        </Button>
      </header>

      {adding && (
        <NewGuideForm
          products={products.data ?? []}
          partnerKeys={partnerKeys}
          onClose={() => setAdding(false)}
        />
      )}

      <nav className="flex flex-wrap gap-6" aria-label="PDF library folders">
        {(
          [
            ["all", "All PDFs"],
            ["guide", "Patient guides"],
            ["coa", "COAs"],
          ] as const
        ).map(([value, label]) => (
          <Button
            key={value}
            type="button"
            size="sm"
            variant={folder === value ? "primary" : "secondary"}
            onClick={() => setFolder(value)}
          >
            <FolderOpen size={12} /> {label}
          </Button>
        ))}
      </nav>

      <label className="relative block w-full max-w-[50rem]">
        <Search
          size={14}
          className="absolute left-10 top-1/2 -translate-y-1/2 text-ink-muted"
          aria-hidden
        />
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search PDFs, pharmacies, lots, or linked treatments"
          className="w-full min-h-38 pl-34 pr-10 border border-line bg-raised type-body-sm outline-none focus:shadow-focus"
        />
      </label>

      {guides.isLoading ? (
        <div className="grid md:grid-cols-2 gap-12 animate-pulse">
          <div className="h-[18rem] bg-sunken" />
          <div className="h-[18rem] bg-sunken" />
        </div>
      ) : guides.isError ? (
        <p className="border border-danger/30 bg-danger-wash p-14 type-body-sm text-danger">
          Guide library could not be loaded.
        </p>
      ) : filteredGuides.length ? (
        <div className="grid xl:grid-cols-2 gap-12 items-start">
          {filteredGuides.map((guide) => (
            <GuideEditor
              key={guide.id}
              guide={guide}
              products={products.data ?? []}
            />
          ))}
        </div>
      ) : (guides.data ?? []).length ? (
        <div className="border border-line-subtle p-28 flex flex-col items-center text-center gap-8">
          <Search size={22} className="text-ink-muted" />
          <p className="type-heading-sm">No matching patient guides</p>
          <p className="type-body-sm text-ink-muted">
            Search by guide title, description, filename, or linked treatment.
          </p>
        </div>
      ) : (
        <div className="border border-line-subtle p-28 flex flex-col items-center text-center gap-8">
          <FileText size={22} className="text-ink-muted" />
          <p className="type-heading-sm">No patient guides yet</p>
          <p className="type-body-sm text-ink-muted">
            Upload the first PDF and link it to the products it supports.
          </p>
        </div>
      )}
    </div>
  );
}

function ProductPicker({
  products,
  selected,
  onChange,
}: {
  products: ClinicianProduct[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const [search, setSearch] = useState("");
  const byId = useMemo(
    () => new Map(products.map((product) => [product.product_id, product])),
    [products],
  );
  const selectedProducts = selected
    .map((productId) => byId.get(productId))
    .filter((product): product is ClinicianProduct => Boolean(product));
  const query = search.trim().toLocaleLowerCase();
  const visibleProducts = products.filter(
    (product) =>
      !query ||
      product.label.toLocaleLowerCase().includes(query) ||
      product.product_id.toLocaleLowerCase().includes(query),
  );
  const selectedSet = new Set(
    selectedProducts.map((product) => product.product_id),
  );
  const toggle = (productId: string) => {
    onChange(
      selectedSet.has(productId)
        ? selectedProducts
            .map((product) => product.product_id)
            .filter((value) => value !== productId)
        : [...selectedProducts.map((product) => product.product_id), productId],
    );
  };

  return (
    <section className="flex flex-col gap-7" aria-label="Linked treatments">
      <div className="flex items-center justify-between gap-8">
        <span className="type-label-xs text-ink-muted">Linked treatments</span>
        <span className="type-label-xs text-ink-muted">
          {selectedProducts.length} selected
        </span>
      </div>

      {selectedProducts.length > 0 && (
        <div className="flex flex-wrap gap-5">
          {selectedProducts.map((product) => (
            <button
              key={product.product_id}
              type="button"
              onClick={() => toggle(product.product_id)}
              title={`Remove ${product.label}`}
              className="inline-flex items-center gap-5 border border-ink bg-sunken px-7 py-4 type-label-xs text-left hover:bg-line-subtle"
            >
              {product.label} <X size={10} aria-hidden />
            </button>
          ))}
        </div>
      )}

      <label className="relative block">
        <Search
          size={13}
          className="absolute left-9 top-1/2 -translate-y-1/2 text-ink-muted"
          aria-hidden
        />
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search treatments or medications"
          className="w-full min-h-36 border border-line bg-raised pl-31 pr-9 type-body-sm outline-none focus:shadow-focus"
        />
      </label>

      <div
        className="max-h-[15rem] overflow-y-auto border border-line-subtle divide-y divide-line-subtle bg-raised"
        role="group"
        aria-label="Treatment results"
      >
        {visibleProducts.map((product) => {
          const checked = selectedSet.has(product.product_id);
          return (
            <label
              key={product.product_id}
              className="flex items-center gap-8 px-9 py-7 cursor-pointer hover:bg-sunken"
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(product.product_id)}
                className="sr-only"
              />
              <span
                aria-hidden
                className={`w-18 h-18 shrink-0 border flex items-center justify-center ${checked ? "border-ink bg-ink text-page" : "border-line bg-raised"}`}
              >
                {checked ? <Check size={11} /> : null}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block type-body-sm font-medium truncate">
                  {product.label}
                </span>
                <span className="block type-label-xs text-ink-muted truncate">
                  {product.product_id}
                </span>
              </span>
            </label>
          );
        })}
        {!products.length && (
          <p className="px-9 py-10 type-body-sm text-ink-muted">
            Treatment catalog is loading…
          </p>
        )}
        {products.length > 0 && !visibleProducts.length && (
          <p className="px-9 py-10 type-body-sm text-ink-muted">
            No treatments match “{search.trim()}”.
          </p>
        )}
      </div>
      <p className="text-10 text-ink-muted">
        Active medications and treatments only. Accessories, supplies, and
        kitting products are excluded.
      </p>
    </section>
  );
}

function NewGuideForm({
  products,
  partnerKeys,
  onClose,
}: {
  products: ClinicianProduct[];
  partnerKeys: string[];
  onClose: () => void;
}) {
  const upload = useUploadClinicianGuide();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [productIds, setProductIds] = useState<string[]>([]);
  const [kind, setKind] = useState<"guide" | "coa">("guide");
  const [coaScope, setCoaScope] = useState<"finished_product" | "raw_material">(
    "finished_product",
  );
  const [partnerKey, setPartnerKey] = useState("");
  const [lotNumber, setLotNumber] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!file) return;
    setError(null);
    try {
      await upload.mutateAsync({
        file,
        title: title.trim(),
        description: description.trim(),
        product_ids: productIds,
        document_kind: kind,
        partner_keys:
          kind === "coa" && coaScope === "finished_product" && partnerKey
            ? [partnerKey]
            : [],
        lot_number: kind === "coa" ? lotNumber.trim() : undefined,
        coa_scope: kind === "coa" ? coaScope : undefined,
      });
      onClose();
    } catch (reason) {
      setError((reason as Error).message || "Guide upload failed.");
    }
  };

  return (
    <section className="border border-ink bg-raised p-12 flex flex-col gap-10">
      <div className="flex items-center justify-between gap-8">
        <h2 className="type-label-sm font-medium">New library PDF</h2>
        <button type="button" onClick={onClose} aria-label="Close upload form">
          <X size={15} />
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        onChange={(event) => {
          const next = event.target.files?.[0] ?? null;
          setFile(next);
          if (next && !title) setTitle(next.name.replace(/\.pdf$/i, ""));
        }}
      />
      <div className="grid md:grid-cols-2 gap-10">
        <div className="flex flex-col gap-8">
          <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
            Document type
            <select
              value={kind}
              onChange={(event) =>
                setKind(event.target.value as "guide" | "coa")
              }
              className="min-h-38 border border-line bg-raised px-9 type-body-sm text-ink"
            >
              <option value="guide">Patient guide</option>
              <option value="coa">Certificate of Analysis</option>
            </select>
          </label>
          {kind === "coa" && (
            <div className="grid sm:grid-cols-2 gap-7">
              <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
                Certificate scope
                <select
                  value={coaScope}
                  onChange={(event) =>
                    setCoaScope(
                      event.target.value as "finished_product" | "raw_material",
                    )
                  }
                  className="min-h-38 border border-line bg-raised px-9 type-body-sm text-ink"
                >
                  <option value="finished_product">Finished product</option>
                  <option value="raw_material">
                    Raw material · reference only
                  </option>
                </select>
              </label>
              <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
                Lot number
                <input
                  value={lotNumber}
                  onChange={(event) => setLotNumber(event.target.value)}
                  placeholder="Optional"
                  className="min-h-38 border border-line px-9 type-body-sm text-ink"
                />
              </label>
            </div>
          )}
          {kind === "coa" && coaScope === "finished_product" && (
            <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
              Dispensing pharmacy
              <select
                value={partnerKey}
                onChange={(event) => setPartnerKey(event.target.value)}
                className="min-h-38 border border-line bg-raised px-9 type-body-sm text-ink"
              >
                <option value="">Select pharmacy…</option>
                {partnerKeys.map((key) => (
                  <option key={key} value={key}>
                    {formatPartner(key)}
                  </option>
                ))}
              </select>
            </label>
          )}
          <Button
            type="button"
            variant="secondary"
            onClick={() => inputRef.current?.click()}
          >
            <FileText size={13} /> {file ? file.name : "Choose PDF"}
          </Button>
          <input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Guide title"
            className="min-h-38 border border-line px-9 type-body-sm outline-none focus:shadow-focus"
          />
          <textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            rows={3}
            placeholder="Short description (optional)"
            className="border border-line px-9 py-7 type-body-sm outline-none resize-y focus:shadow-focus"
          />
        </div>
        <ProductPicker
          products={products}
          selected={productIds}
          onChange={setProductIds}
        />
      </div>
      <div className="flex items-center justify-between gap-8">
        <span className="text-11 text-danger">{error}</span>
        <Button
          type="button"
          size="sm"
          loading={upload.isPending}
          disabled={
            !file ||
            !title.trim() ||
            (kind === "guide" && productIds.length === 0) ||
            (kind === "coa" &&
              coaScope === "finished_product" &&
              (productIds.length === 0 || !partnerKey))
          }
          onClick={submit}
        >
          <Upload size={12} /> Upload PDF
        </Button>
      </div>
    </section>
  );
}

function GuideEditor({
  guide,
  products,
}: {
  guide: ClinicianGuide;
  products: ClinicianProduct[];
}) {
  const update = useUpdateClinicianGuide();
  const [title, setTitle] = useState(guide.title);
  const [description, setDescription] = useState(guide.description ?? "");
  const [productIds, setProductIds] = useState(guide.product_ids);
  const [preview, setPreview] = useState(false);
  const [privatePreviewUrl, setPrivatePreviewUrl] = useState<string | null>(
    null,
  );
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const productById = useMemo(
    () => new Map(products.map((product) => [product.product_id, product])),
    [products],
  );

  useEffect(() => {
    if (!products.length) return;
    const allowed = new Set(products.map((product) => product.product_id));
    setProductIds((current) =>
      current.filter((productId) => allowed.has(productId)),
    );
  }, [products]);

  useEffect(
    () => () => {
      if (privatePreviewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(privatePreviewUrl);
      }
    },
    [privatePreviewUrl],
  );

  useEffect(() => {
    if (!guide.content_url) return;
    let cancelled = false;
    apiBlob(guide.content_url)
      .then((blob) => {
        if (cancelled) return;
        setPrivatePreviewUrl(URL.createObjectURL(blob));
      })
      .catch(() => {
        if (!cancelled) setPreviewError("PDF preview could not be loaded.");
      });
    return () => {
      cancelled = true;
    };
  }, [guide.content_url]);

  const openPreview = async () => {
    setPreviewError(null);
    if (guide.view_url) {
      setPrivatePreviewUrl(guide.view_url);
      setPreview(true);
      return;
    }
    if (privatePreviewUrl) {
      setPreview(true);
      return;
    }
    if (!guide.content_url) return;
    try {
      const blob = await apiBlob(guide.content_url);
      if (privatePreviewUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(privatePreviewUrl);
      }
      setPrivatePreviewUrl(URL.createObjectURL(blob));
      setPreview(true);
    } catch {
      setPreviewError("PDF preview could not be loaded.");
    }
  };

  const save = async () => {
    setMessage(null);
    try {
      await update.mutateAsync({
        id: guide.id,
        title: title.trim(),
        description: description.trim(),
        product_ids: productIds,
      });
      setMessage("Saved");
      setEditing(false);
    } catch {
      setMessage("Could not save");
    }
  };

  const cancel = () => {
    const allowed = new Set(products.map((product) => product.product_id));
    setTitle(guide.title);
    setDescription(guide.description ?? "");
    setProductIds(
      products.length
        ? guide.product_ids.filter((productId) => allowed.has(productId))
        : guide.product_ids,
    );
    setMessage(null);
    setEditing(false);
  };

  const cardPreviewUrl = guide.view_url || privatePreviewUrl
    ? `${guide.view_url || privatePreviewUrl}#page=1&toolbar=0&navpanes=0&scrollbar=0`
    : null;

  return (
    <>
      <article className="border border-line-subtle bg-raised overflow-hidden">
        <div className="relative block w-full h-[14rem] border-b border-line-subtle bg-sunken overflow-hidden group">
          {cardPreviewUrl ? (
            <iframe
              title={`${guide.title} first page`}
              src={cardPreviewUrl}
              tabIndex={-1}
              className="w-full h-[22rem] border-0 pointer-events-none origin-top"
            />
          ) : (
            <span className="h-full flex items-center justify-center text-ink-muted">
              <FileText size={24} />
            </span>
          )}
          {(guide.view_url || guide.content_url) && (
            <button
              type="button"
              onClick={openPreview}
              className="absolute inset-0 flex items-end justify-end p-8 focus-visible:outline-none focus-visible:shadow-focus"
              aria-label={`Preview ${guide.title}`}
            >
              <span className="inline-flex items-center gap-5 bg-ink text-page px-8 py-5 type-label-xs opacity-90 group-hover:opacity-100">
                <Eye size={11} /> Open PDF
              </span>
            </button>
          )}
        </div>

        <div className="p-12 flex flex-col gap-9">
          <div className="flex items-start gap-8">
            {guide.document_kind === "coa" ? (
              <ShieldCheck size={17} className="mt-5 shrink-0" />
            ) : (
              <FileText size={17} className="mt-5 shrink-0" />
            )}
            <div className="min-w-0 flex-1">
              {editing ? (
                <input
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  aria-label="Guide title"
                  className="w-full border-b border-line bg-transparent py-3 type-body-sm font-medium outline-none focus:border-ink"
                />
              ) : (
                <h2 className="type-body-sm font-medium">{guide.title}</h2>
              )}
              <p className="text-11 text-ink-muted truncate mt-4">
                {guide.filename}
              </p>
              <div className="flex flex-wrap gap-5 mt-6">
                <span className="border border-line bg-sunken px-6 py-3 type-label-xs">
                  {guide.document_kind === "coa" ? "COA" : "PATIENT GUIDE"}
                </span>
                {guide.coa_scope === "finished_product" && (
                  <span className="border border-success/40 bg-success-wash px-6 py-3 type-label-xs text-success">
                    FINISHED PRODUCT
                  </span>
                )}
                {guide.coa_scope === "raw_material" && (
                  <span className="inline-flex items-center gap-4 border border-warning/50 bg-warning-wash px-6 py-3 type-label-xs text-warning">
                    <AlertTriangle size={10} /> REFERENCE ONLY
                  </span>
                )}
              </div>
            </div>
            {!editing && (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => {
                  setMessage(null);
                  setEditing(true);
                }}
              >
                <Pencil size={12} /> Edit
              </Button>
            )}
          </div>

          {editing ? (
            <textarea
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={2}
              placeholder="Description"
              className="border border-line bg-sunken px-9 py-7 type-body-sm outline-none resize-y focus:shadow-focus"
            />
          ) : (
            <p className="type-body-sm text-ink-secondary min-h-20">
              {guide.description || "No description."}
            </p>
          )}

          {guide.document_kind === "coa" && !editing && (
            <dl className="grid sm:grid-cols-2 gap-7 border border-line-subtle bg-sunken p-9">
              <div>
                <dt className="type-label-xs text-ink-muted">Pharmacy</dt>
                <dd className="type-body-sm mt-2">
                  {guide.partner_keys.length
                    ? guide.partner_keys.map(formatPartner).join(", ")
                    : "Raw-material manufacturer"}
                </dd>
              </div>
              <div>
                <dt className="type-label-xs text-ink-muted">Lot</dt>
                <dd className="type-body-sm mt-2">
                  {guide.lot_number || "Not listed"}
                </dd>
              </div>
            </dl>
          )}

          {editing ? (
            <ProductPicker
              products={products}
              selected={productIds}
              onChange={setProductIds}
            />
          ) : (
            <section
              className="flex flex-col gap-6"
              aria-label="Linked treatments"
            >
              <span className="type-label-xs text-ink-muted">
                Linked treatments
              </span>
              <div className="flex flex-wrap gap-5">
                {guide.product_ids.map((productId) => (
                  <span
                    key={productId}
                    title={productId}
                    className="inline-flex items-center border border-line bg-sunken px-7 py-4 type-label-xs"
                  >
                    {productById.get(productId)?.label ?? productId}
                  </span>
                ))}
                {!guide.product_ids.length && (
                  <span className="type-body-sm text-ink-muted">
                    Reference only · not linked for automatic delivery
                  </span>
                )}
              </div>
            </section>
          )}

          {editing && (
            <div className="flex flex-wrap items-center justify-between gap-8">
              <span
                className={`text-11 ${message === "Saved" ? "text-success" : "text-danger"}`}
              >
                {message}
              </span>
              <div className="flex flex-wrap items-center gap-6">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={cancel}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  loading={update.isPending}
                  onClick={() => {
                    if (window.confirm(`Archive “${guide.title}”?`)) {
                      update.mutate({ id: guide.id, active: false });
                    }
                  }}
                >
                  <Archive size={12} /> Archive
                </Button>
                <Button
                  type="button"
                  size="sm"
                  loading={update.isPending}
                  disabled={
                    !title.trim() ||
                    (guide.coa_scope !== "raw_material" &&
                      productIds.length === 0)
                  }
                  onClick={save}
                >
                  <Save size={12} /> Save
                </Button>
              </div>
            </div>
          )}
        </div>
      </article>
      <Drawer
        open={preview}
        onClose={() => setPreview(false)}
        title={guide.title}
      >
        {privatePreviewUrl ? (
          <iframe
            title={guide.title}
            src={privatePreviewUrl}
            className="w-full h-[calc(100vh-6rem)] border-0"
          />
        ) : (
          <p className="p-12 type-body-sm text-danger">
            {previewError || "PDF preview unavailable."}
          </p>
        )}
      </Drawer>
      {previewError && !preview && (
        <p className="type-body-sm text-danger">{previewError}</p>
      )}
    </>
  );
}
